import {
  describeHybridTransportError,
  HybridTransportError,
} from '@application/backup/HybridBackupTransport';
import type { HybridRestoreErrorCode } from '@application/backup/HybridRestoreService';
import { PASSPHRASE_MIN_LENGTH } from '@application/backup/HybridBackupCrypto';
import { issuerName } from '@domain/legal/IssuerInfo';
import { getIssuerInfo } from '@infra/legal/IssuerInfoService';

/** Codes d’erreur UI (restauration + configuration phrase secrète). */
export type HybridUiErrorCode = HybridRestoreErrorCode | 'PASSPHRASE_TOO_SHORT';

/**
 * Message clair (français) pour l’interface Paramètres Hybride.
 * Réutilise `describeHybridTransportError` pour les codes issus du serveur / transport.
 */
export function describeHybridUiError(
  code: HybridUiErrorCode | string,
  fallbackMessage?: string,
): string {
  switch (code) {
    case 'RESTORE_WINDOW_CLOSED':
      return describeHybridTransportError(
        new HybridTransportError('AUTH', '', 403, 'restore_window_closed'),
      );
    case 'LICENSE_REVOKED':
      return describeHybridTransportError(
        new HybridTransportError('AUTH', '', 403, 'license_revoked'),
      );
    case 'SERVER_INTEGRITY_ERROR':
      return describeHybridTransportError(
        new HybridTransportError('SERVER', '', 500, 'integrity_error'),
      );
    case 'BACKUP_NOT_FOUND':
      return describeHybridTransportError(
        new HybridTransportError('NOT_FOUND', '', 404, 'not_found'),
      );
    case 'DOWNLOAD_FAILED':
      return describeHybridTransportError(new HybridTransportError('NETWORK', ''));

    case 'WRONG_SECRET':
      return 'Phrase secrète ou clé de récupération incorrecte. Vérifiez votre saisie et réessayez.';
    case 'RECOVERY_KEY_INVALID':
      return 'La clé de récupération est invalide ou incomplète. Vérifiez que vous avez bien recopié toute la clé.';
    case 'AUTH_FAILED':
      return 'Sauvegarde altérée ou corrompue : le contrôle d’authenticité a échoué. Essayez une autre sauvegarde.';
    case 'HASH_MISSING':
      return 'Le serveur n’a pas fourni l’empreinte de contrôle : restauration refusée pour votre sécurité.';
    case 'HASH_MISMATCH':
      return 'Empreinte de contrôle différente : le fichier reçu est corrompu ou a été altéré. Réessayez ou choisissez une autre sauvegarde.';
    case 'UNSUPPORTED_FORMAT':
      return 'Format de sauvegarde non reconnu par cette version de Nexxus ERP.';
    case 'ENVELOPE_INVALID':
      return 'Fichier de sauvegarde illisible ou incomplet.';
    case 'CONTENT_INVALID':
      return 'Contenu de la sauvegarde illisible ou incohérent. Choisissez une autre sauvegarde.';
    case 'SAFETY_BACKUP_FAILED':
      return 'Impossible de créer la sauvegarde de sécurité des données actuelles : restauration annulée, rien n’a été modifié.';
    case 'IMPORT_FAILED':
      return 'L’import a échoué : tout a été annulé, vos données actuelles sont intactes.';
    case 'INTEGRITY_MISMATCH':
      return 'Contrôle d’intégrité en échec après import. La sauvegarde de sécurité de vos anciennes données a été conservée.';
    case 'BACKUP_TENANT_MISMATCH':
      // Préférer le message service (nomme l’entreprise de la sauvegarde) s’il est fourni.
      if (fallbackMessage && fallbackMessage.trim().length > 0) return fallbackMessage;
      return 'Cette sauvegarde appartient à une autre entreprise : restauration refusée, rien n’a été modifié. Ouvrez d’abord l’entreprise concernée, ou confirmez un transfert d’entreprise si c’est volontaire.';
    case 'PASSPHRASE_TOO_SHORT':
      return `La phrase doit contenir au moins ${String(PASSPHRASE_MIN_LENGTH)} caractères.`;

    default:
      if (fallbackMessage && fallbackMessage.trim().length > 0) return fallbackMessage;
      return `Une erreur est survenue pendant l’opération de sauvegarde. Réessayez ou contactez ${issuerName(getIssuerInfo())}.`;
  }
}
